import { Link, useNavigate } from "react-router-dom";
import { Divider, Modal, Spinner } from "@whitespace/whitespace-ui";
import { useStateMachine } from "little-state-machine";
import {
  Breadcrumb, Button, Card, Col, Row
} from "react-bootstrap";
import { updateIsUserHistoryFilterDrawerOpen } from "actions/updateIsUserHistoryFilterDrawerOpen";
import { updateSelectedUser } from "actions/updateSelectedUser";
import { useEffect, useState } from "react";
import Groups from "components/Groups/Groups";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import IGroupResponseItem from "interfaces/responses/IGroupResponseItem";

const ManageUserGroups = () => {
  const { state } = useStateMachine();
  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateIsUserHistoryFilterDrawerOpen,
    updateSelectedUser
  });

  const {
    isSending: removeGroupsIsSending, errors: removeGroupsErrors, sendRequest: removeGroupsSendRequest, statusCode: removeGroupsStatusCode
  } = useAmplifyREST();

  const {
    isSending: addGroupsIsSending, errors: addGroupsErrors, sendRequest: addGroupsSendRequest, statusCode: addGroupsStatusCode
  } = useAmplifyREST();

  const {
    isSending: getGroupsIsSending, errors: getGroupsErrors, data: getGroupsData, sendRequest: getGroupsSendRequest
  } = useAmplifyREST();

  const [groupsSelected, setGroupsSelected] = useState<string[]>(state.selectedUser?.groups ? state.selectedUser.groups.map(group => group.groupId) : []);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

  const isSending = getGroupsIsSending || removeGroupsIsSending || addGroupsIsSending;
  const errors = getGroupsErrors || removeGroupsErrors || addGroupsErrors;

  const handleGroupSelectionChange = (updatedGroupsSelected: string[]) => {
    setGroupsSelected([]);
    setGroupsSelected(updatedGroupsSelected);
  };

  const groupsUrl = `users/${encodeURIComponent(state.selectedUser!.userId)}/groups`;

  const findGroupById = (id: string): IGroupResponseItem | undefined => {
    if (getGroupsData) {
      return (getGroupsData as IGroupResponseItem[]).find(group => group.id === id);
    }
    return undefined;
  };

  const checkForRemoveModal = (): boolean => {
    let isAppsToRemove = false;
    const groupsToRemove = state.selectedUser!.groups!.map(group => group.groupId).filter(userGroup => !groupsSelected.includes(userGroup));
    groupsToRemove.forEach(grp => {
      let nextId: string | null = grp;
      do {
        const groupModel = findGroupById(nextId);
        nextId = groupModel?.pId ?? null;
        if (nextId === null) {
          isAppsToRemove = true;
          break;
        } else if (nextId !== null && groupsSelected.includes(nextId)) {
          break;
        }
      } while (nextId !== null);
    });

    return isAppsToRemove;
  };

  const removeGroups = () => {
    const groupsToRemove = state.selectedUser!.groups!.map(group => group.groupId).filter(userGroup => !groupsSelected.includes(userGroup));

    if (groupsToRemove.length > 0) {
      removeGroupsSendRequest(groupsUrl, "DELETE", groupsToRemove);
    } else {
      navigate("/manageuser");
    }
  };

  const addGroups = () => {
    const groupsToAdd = groupsSelected.filter(selectedGroup => !state.selectedUser!.groups!.map(group => group.groupId).includes(selectedGroup));
    const groupsToRemove = state.selectedUser!.groups!.map(group => group.groupId).filter(userGroup => !groupsSelected.includes(userGroup));

    if (groupsToRemove.length > 0 && checkForRemoveModal() && !showRemoveModal) {
      setShowRemoveModal(true);
    } else if (groupsToAdd.length > 0) {
      addGroupsSendRequest(groupsUrl, "PUT", groupsToAdd);
    } else {
      removeGroups();
    }
  };

  const handleSaveClick = () => {
    addGroups();
  };

  useEffect(() => {
    if (removeGroupsStatusCode === 204) {
      navigate("/manageuser");
    }
  }, [removeGroupsStatusCode]);

  useEffect(() => {
    if (addGroupsStatusCode === 204) {
      removeGroups();
    }
  }, [addGroupsStatusCode]);

  useEffect(() => {
    getGroupsSendRequest("groups", "GET");
  }, [getGroupsSendRequest]);

  const handleNavToUsers = () => {
    actions.updateSelectedUser(undefined);
    navigate("/users");
  };

  return (
    <>
      {errors && <AmplifyError errors={errors} />}
      {state.selectedUser
        && (
          <Card className="card-user-management">
            <Card.Header>
              <Row className="w-100">
                <Col xs={8}>
                  <h1>
                    Manage User - Groups
                    <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
                  </h1>
                </Col>
                <Col xs={4} className="d-flex justify-content-end">
                  <span className="selected-user-title">{state.selectedUser?.username}</span>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="no-padding">
              <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={8} className="d-flex">
                  <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                    <Breadcrumb.Item data-testid="breadcrumb-users" linkAs={Link} linkProps={{ to: "/" }} onClick={handleNavToUsers}>Users</Breadcrumb.Item>
                    <Breadcrumb.Item
                      data-testid="breadcrumb-users"
                      linkAs={Link}
                      linkProps={{ to: "/" }}
                      onClick={() => navigate("/manageuser")}
                    >
                      Manage User
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>Groups</Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
                <Col xs={4} className="d-flex justify-content-end user-filter">
                  <Button
                    variant="primary"
                    className="me-2"
                    onClick={handleSaveClick}
                    disabled={isSending || groupsSelected.length === 0}
                  >
                    Save
                  </Button>
                  <Button variant="secondary" onClick={() => navigate("/manageuser")}>
                    Back
                  </Button>
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col xs={12}>
                  <Groups
                    groupsSelected={state.selectedUser.groups?.map(group => group.groupId)!}
                    groupsData={getGroupsData}
                    handleGroupSelectionChange={handleGroupSelectionChange}
                    isSending={getGroupsIsSending}
                    errors={getGroupsErrors}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      {showRemoveModal && (
        <Modal title="Remove Groups" showBackdrop closeEvent={() => setShowRemoveModal(false)}>
          <Row className="mb-3">
            <Col>
              This action will remove allocation to all associated applications. Select the Cancel option if you do not want this to occur
            </Col>
          </Row>
          <Row>
            <Col className="text-end">
              <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>Cancel</Button>
            </Col>
            <Col>
              <Button variant="primary" onClick={() => { addGroups(); setShowRemoveModal(false); }}>Yes</Button>
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export default ManageUserGroups;
